import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import { IniInfo } from "system/types/interfaces";
import { ApplicabilityRuleBuilder } from "../applicabilityTab/ApplicabilityRuleBuilder";
import { useApi, useConfiguration } from "system/hooks";
import { getConfigValueError, unwrapOuterQuotes } from "components/modules/policy-editor/utils/configValueQuoting";

interface ConfigRuleApplicabilityModalProps {
  open: boolean;
  onClose: () => void;
  initialRule: IniInfo;
  onFinish: (finalRule: IniInfo) => string | void;
}

export const ConfigRuleApplicabilityModal: React.FC<ConfigRuleApplicabilityModalProps> = ({ open, onClose, initialRule, onFinish }) => {
  const currentTenant = useConfiguration().currentTenant;

  const [localRule, setLocalRule] = useState<IniInfo>(initialRule);
  const [valueError, setValueError] = useState<string>("");
  const [isValid, setIsValid] = useState<boolean>(true);
  const [showDecrypted, setShowDecrypted] = useState<boolean>(false);

  const api = useApi();

  useEffect(() => {
    const initializeValue = async () => {
      // A comment holds the whole raw line, so the fallback below would encrypt it and lose it.
      if (open && !localRule.isComment && localRule.type === "string" && localRule.valueShouldBeLogged === false) {
        // The stored value is quoted; the ciphertext has to go to the API without the wrapper.
        const stored = unwrapOuterQuotes(localRule.value || "");
        if (stored !== localRule.value) {
          setLocalRule((prev) => ({ ...prev, value: stored }));
        }

        const decryptResponse = await api.encryption.decrypt(currentTenant?.shortName ?? "", stored);
        if (decryptResponse.isSuccess && decryptResponse.value !== stored) {
          setShowDecrypted(true);
        } else {
          const encryptResponse = await api.encryption.encrypt(currentTenant?.shortName ?? "", stored);
          if (encryptResponse.isSuccess) {
            setLocalRule((prev) => ({ ...prev, value: encryptResponse.value ?? prev.value }));
            setShowDecrypted(true);
          } else {
            setValueError("Initialization encryption failed: " + encryptResponse.failMessage);
            setShowDecrypted(false);
          }
        }
      }
    };
    initializeValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, localRule.valueShouldBeLogged, currentTenant?.shortName, localRule.type, api.encryption]);

  const handleValueChange = (newValue: string) => {
    setValueError("");
    setLocalRule((prev) => ({ ...prev, value: newValue }));
  };

  const handleToggleDecrypted = async () => {
    if (showDecrypted) {
      const decryptResponse = await api.encryption.decrypt(currentTenant?.shortName ?? "", localRule.value || "");
      if (decryptResponse.isSuccess) {
        setLocalRule((prev) => ({ ...prev, value: decryptResponse.value || "" }));
      } else {
        setValueError("Decryption failed: " + decryptResponse.failMessage);
      }
    } else {
      const encryptResponse = await api.encryption.encrypt(currentTenant?.shortName ?? "", localRule.value || "");
      if (encryptResponse.isSuccess) {
        setLocalRule((prev) => ({ ...prev, value: encryptResponse.value ?? prev.value }));
      } else {
        setValueError("Re-encryption failed: " + encryptResponse.failMessage);
      }
    }
    setShowDecrypted(!showDecrypted);
  };

  const handleBuilderChange = (result: {
    finalValue: string;
    finalDelimiter: string;
    comment: string;
    fullSyntax: string;
    isValid: boolean;
  }) => {
    if (!result.isValid) {
      setIsValid(result.isValid);
      return;
    }
    setIsValid(result.isValid);

    if (
      localRule.applicability === result.finalValue &&
      localRule.delimiter === result.finalDelimiter &&
      localRule.inlineComment === result.comment
    ) {
      return;
    }

    setLocalRule((prev) => ({
      ...prev,
      applicability: result.finalValue,
      delimiter: result.finalDelimiter,
      inlineComment: result.comment,
    }));
  };

  const handleUpdate = async () => {
    if (localRule.type === "string" && localRule.regex) {
      const pattern = new RegExp(`^${localRule.regex}$`, "i");

      if (showDecrypted) {
        const decryptResponse = await api.encryption.decrypt(currentTenant?.shortName ?? "", localRule.value || "");
        if (decryptResponse.isSuccess && !pattern.test(decryptResponse.value || "")) {
          setValueError(`The provided value does not match the pattern:\n${localRule.regex}`);
          setShowDecrypted(false);
          handleToggleDecrypted();
          return;
        }
      } else {
        // The pattern describes the value, not the quoting the ini format adds around it.
        if (!pattern.test(unwrapOuterQuotes(localRule.value || ""))) {
          setValueError(`The provided value does not match the pattern:\n${localRule.regex}`);
          return;
        }
      }
    }

    let finalValue = localRule.value;
    if (localRule.type === "string" && !showDecrypted && localRule.valueShouldBeLogged === false) {
      const encryptResponse = await api.encryption.encrypt(currentTenant?.shortName ?? "", localRule.value || "");
      if (encryptResponse.isSuccess) {
        finalValue = encryptResponse.value ?? localRule.value;
      } else {
        setValueError("Final encryption failed: " + encryptResponse.failMessage);
        return;
      }
    }

    const quotingError = getConfigValueError(
      localRule.name ?? "",
      finalValue ?? "",
      localRule.delimiter ?? "",
      localRule.applicability ?? ""
    );
    if (quotingError) {
      setValueError(quotingError);
      return;
    }

    const response = onFinish({ ...localRule, value: finalValue });

    if (typeof response === "string") {
      setValueError(response);
    }
  };

  const renderValueField = () => {
    // Check if it’s the special “Pre-Run” or “Post-Run”
    if (localRule.name === "Pre-run Command" || localRule.name === "Post-run Command") {
      return (
        <TextField
          label="Value"
          multiline
          rows={1}
          fullWidth
          value={localRule.value || ""}
          onChange={(e) => handleValueChange(e.target.value)}
          error={!!valueError}
          helperText={valueError}
          sx={{ mt: 1 }}
        />
      );
    }

    // If we have a “type” on localRule, handle bool / enum / string
    if (localRule.type === "bool") {
      return (
        <>
          <FormControlLabel
            label="Value"
            labelPlacement="top"
            sx={{ color: "#fff", alignItems: "flex-start", mt: 1 }}
            control={
              <Stack direction="row" spacing={0} alignItems="center">
                <Typography sx={{ fontSize: "14px", opacity: localRule.value === "false" ? 1 : 0.4 }}>False</Typography>
                <Switch checked={localRule.value === "true"} onChange={(e) => handleValueChange(e.target.checked ? "true" : "false")} />
                <Typography sx={{ fontSize: "14px", opacity: localRule.value === "true" ? 1 : 0.4 }}>True</Typography>
              </Stack>
            }
          />
          {valueError && (
            <Typography variant="body2" color="error">
              {valueError}
            </Typography>
          )}
        </>
      );
    }

    if (localRule.type === "enum") {
      const enumOptions = localRule.regex ? localRule.regex.replace(/[()]/g, "").split("|") : [];

      return (
        <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
          <InputLabel>Value</InputLabel>
          <Select label="Value" value={localRule.value || ""} onChange={(e) => handleValueChange(e.target.value as string)}>
            {enumOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          {valueError && (
            <Typography variant="body2" color="error">
              {valueError}
            </Typography>
          )}
        </FormControl>
      );
    }

    // Default => multiline string
    return (
      <>
        <TextField
          label="Value"
          multiline
          rows={1}
          fullWidth
          value={localRule.value || ""}
          onChange={(e) => handleValueChange(e.target.value)}
          error={!!valueError}
          helperText={valueError}
          sx={{ mt: 1 }}
          disabled={showDecrypted}
        />
        {localRule.type === "string" && localRule.valueShouldBeLogged === false && (
          <FormControlLabel
            control={<Switch checked={showDecrypted} onChange={handleToggleDecrypted} />}
            label={`${showDecrypted ? "Decrypt" : "Encrypt"} Value`}
            sx={{ mt: 1, color: "#fff" }}
          />
        )}
      </>
    );
  };

  // A commented row has no structure to build, so it is edited as plain text.
  if (localRule.isComment) {
    const commitComment = () => {
      const text = (localRule.value ?? "").trim();
      onFinish({ ...localRule, value: text.startsWith(";") ? text : `;${text}` });
    };

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#fff" }}>Edit Comment</DialogTitle>
        <DialogContent sx={{ backgroundColor: "#1b1a19" }} dividers>
          <TextField
            label="Comment"
            multiline
            rows={4}
            fullWidth
            value={localRule.value || ""}
            onChange={(e) => handleValueChange(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
            The line is written to the policy exactly as typed. Uncommenting it later is only offered when the text can be read back
            as a configuration.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1b1a19" }}>
          <Button variant="outlined" onClick={onClose}>
            Discard
          </Button>
          <Button variant="contained" onClick={commitComment}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#fff" }}>Edit Configuration Rule</DialogTitle>
      <DialogContent sx={{ backgroundColor: "#1b1a19" }} dividers>
        {/* Show some metadata about the rule */}
        <Box mb={2}>
          <Typography variant="h6">[{localRule?.name}]</Typography>
          <Typography variant="subtitle2" sx={{ opacity: 0.7 }} gutterBottom>
            {localRule?.description}
          </Typography>

          <Box mt={2}>{renderValueField()}</Box>
        </Box>

        {/* Then the applicability builder */}
        <Box mt={3}>
          <ApplicabilityRuleBuilder
            initialName={localRule.name}
            initialNameValue={localRule.value}
            initialDelimiter={localRule.delimiter}
            initialValue={localRule.applicability}
            initialComment={localRule.inlineComment || ""}
            isUpdating={true}
            onChange={handleBuilderChange}
            shouldDisplayComment={false}
            shouldDisplayNameValue={true}
          />
        </Box>

        {/* Possibly also show a “description” if relevant */}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#1b1a19" }}>
        <Button variant="outlined" onClick={onClose}>
          Discard
        </Button>
        <Button variant="contained" onClick={handleUpdate} disabled={!isValid}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};
